import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, Gear } from '@phosphor-icons/react';
import { CONSENT_KEY, CONSENT_EVENT, initPixelIfConsented } from '../utils/metaPixel';
import { saveConsent, getConsent } from '../utils/consent';

/**
 * Two-mode cookie consent banner.
 *   • Default mode  — Accept all / Reject optional / Customize
 *   • Customize     — per-category toggles for Analytics + Advertising
 *
 * Consent is written under localStorage['gospeleon.cookieConsent'] using the
 * v2 JSON shape ({analytics, advertising}). Legacy "all"/"necessary" values
 * from older visitors are read unchanged (see utils/consent.js).
 */
export function CookieConsentBanner() {
  const [decided, setDecided] = useState(true); // default true → hidden until mounted check
  const [customize, setCustomize] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [advertising, setAdvertising] = useState(false);

  useEffect(() => {
    const state = getConsent();
    setDecided(state.decided);
    setAnalytics(state.analytics);
    setAdvertising(state.advertising);
    if (state.analytics) initPixelIfConsented();
  }, []);

  const persist = useCallback((next) => {
    saveConsent(next);
    setDecided(true);
    // metaPixel + AdSense loaders listen on CONSENT_EVENT via saveConsent
    if (next.analytics) initPixelIfConsented();
  }, []);

  const acceptAll = () => persist({ analytics: true, advertising: true });
  const rejectOptional = () => persist({ analytics: false, advertising: false });
  const savePreferences = () => persist({ analytics, advertising });

  if (decided) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-md z-[60]"
      data-testid="cookie-consent-banner"
    >
      <div className="rounded-2xl bg-[#141417] border border-white/10 shadow-2xl p-5 backdrop-blur-md">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-9 h-9 rounded-full bg-[#F59E0B]/10 flex items-center justify-center">
            <Cookie className="text-[#F59E0B]" size={18} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-white text-sm font-semibold mb-1">Cookies on Gospeleon</h3>
            <p className="text-white/60 text-xs leading-relaxed">
              Essential cookies keep you signed in. Analytics helps us measure what listeners
              love, and Advertising cookies fund the platform via personalised ads. Change your
              choices any time via our{' '}
              <Link to="/cookie-policy" className="text-[#F59E0B] hover:underline">
                Cookie Policy
              </Link>.
            </p>
          </div>
          <button
            type="button"
            onClick={rejectOptional}
            className="shrink-0 text-white/40 hover:text-white/70 p-1 -mt-1 -mr-1"
            aria-label="Dismiss and reject optional cookies"
            data-testid="cookie-consent-dismiss"
          >
            <X size={16} />
          </button>
        </div>

        {customize ? (
          <div className="mt-4 space-y-3" data-testid="cookie-consent-customize">
            <ToggleRow
              label="Analytics"
              hint="Meta Pixel + PostHog — helps us see which songs resonate."
              value={analytics}
              onChange={setAnalytics}
              testID="cookie-toggle-analytics"
            />
            <ToggleRow
              label="Advertising"
              hint="Google AdSense — personalised ads to keep Gospeleon free."
              value={advertising}
              onChange={setAdvertising}
              testID="cookie-toggle-advertising"
            />
            <div className="flex flex-col sm:flex-row gap-2 mt-2">
              <button
                type="button"
                onClick={() => setCustomize(false)}
                className="flex-1 px-4 py-2 rounded-full border border-white/15 text-white/70 hover:bg-white/5 text-sm"
                data-testid="cookie-consent-back"
              >
                Back
              </button>
              <button
                type="button"
                onClick={savePreferences}
                className="flex-1 px-4 py-2 rounded-full bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-black text-sm font-medium"
                data-testid="cookie-consent-save"
              >
                Save preferences
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row gap-2 mt-4">
              <button
                type="button"
                onClick={rejectOptional}
                className="flex-1 px-4 py-2 rounded-full border border-white/15 text-white/80 hover:bg-white/5 text-sm transition-colors"
                data-testid="cookie-consent-reject"
              >
                Reject optional
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="flex-1 px-4 py-2 rounded-full bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-black text-sm font-medium transition-colors"
                data-testid="cookie-consent-accept"
              >
                Accept all
              </button>
            </div>
            <button
              type="button"
              onClick={() => setCustomize(true)}
              className="mt-3 flex items-center gap-1.5 text-xs text-white/60 hover:text-[#F59E0B] mx-auto"
              data-testid="cookie-consent-customize-btn"
            >
              <Gear size={14} /> Customize
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function ToggleRow({ label, hint, value, onChange, testID }) {
  return (
    <div className="flex items-start gap-3 py-1">
      <div className="flex-1 min-w-0">
        <p className="text-white text-xs font-medium">{label}</p>
        <p className="text-white/40 text-[11px] leading-snug mt-0.5">{hint}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        onClick={() => onChange(!value)}
        className={`shrink-0 w-10 h-6 rounded-full border transition-colors ${
          value
            ? 'bg-[#F59E0B] border-[#F59E0B]'
            : 'bg-white/5 border-white/15'
        }`}
        data-testid={testID}
      >
        <span
          className={`block w-4 h-4 rounded-full bg-white shadow transition-transform ${
            value ? 'translate-x-5' : 'translate-x-0.5'
          } mt-0.5`}
        />
      </button>
    </div>
  );
}

// Re-export legacy hooks so unchanged callers keep working.
export { CONSENT_KEY, CONSENT_EVENT };
